import { useEffect } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";

import {
  useDisclosure,
  useRemoveSupplierCustomer,
  useSupplierCustomers,
} from "hooks";

import { CustomerRow } from "./components/CustomerRow";
import { EvotruxDivider } from "../../../EvotruxDivider/EvotruxDivider";

interface Props {
  refresh: boolean;
  onRefreshComplete: () => void;
  onAddCustomer: () => void;
}

export function CustomerNetworkTable(props: Props) {
  const { refresh, onRefreshComplete, onAddCustomer } = props;
  const { data, loading, error, refetch } = useSupplierCustomers();
  const {
    loading: removeLoading,
    success: removeSuccess,
    removeCustomer,
    resetState,
  } = useRemoveSupplierCustomer();
  const {
    isOpen: snackbarOpened,
    onClose: onSnackbarClose,
    onOpen: openSnackbar,
  } = useDisclosure(false);

  useEffect(() => {
    if (removeSuccess) {
      openSnackbar();
      refetch();
      resetState();
    }
  }, [removeSuccess, refetch, resetState, openSnackbar]);

  useEffect(() => {
    if (refresh) {
      refetch();
      onRefreshComplete();
    }
  }, [refresh, refetch, onRefreshComplete]);

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack>
        <Typography color="red">{error}</Typography>
      </Stack>
    );
  }

  if (data.length === 0) {
    return (
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight="600">
            Customers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Shippers you send freight to
          </Typography>
        </Stack>
        <EvotruxDivider />

        <Stack alignItems="center" py={4} spacing={2}>
          <Typography variant="body1" color="text.secondary">
            No customers in your network yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add customers to notify them about their shipments.
          </Typography>
          <Button variant="contained" onClick={onAddCustomer} sx={{ mt: 2 }}>
            Add Customer
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight="600">
              Customers ({data.length})
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Shippers you send freight to
            </Typography>
          </Stack>

          <Button
            variant="contained"
            onClick={onAddCustomer}
            sx={{ minWidth: "140px" }}
          >
            Add Customer
          </Button>
        </Stack>
        <EvotruxDivider />

        <Box
          sx={{
            maxHeight: "500px",
            overflowY: "scroll",
          }}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Date Added</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((customer) => (
                  <CustomerRow
                    key={customer.id}
                    customer={customer}
                    onRemove={removeCustomer}
                    loading={removeLoading}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
      <Snackbar
        open={snackbarOpened}
        autoHideDuration={4000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Customer removed successfully!
        </Alert>
      </Snackbar>
    </>
  );
}
